import { useQuery } from "@tanstack/react-query";
import { sgRealtimeAuthHeaders } from "./realtimeAuth";

// Plataformas donde está dado de alta cada IMEI (v5, Wialon, Mapon), reporte o
// no. Lo calcula sg-realtime (POST /reporting/origins) con los rosters de las
// tres; reemplaza a users.origin, que se elige a mano, y al nombre/apellido del
// usuario, donde se escribía la plataforma a mano (medido el 19-oct-2026: 2246
// de 2724 usuarios de SG traen «WIALON | ARRENDAMIENTO», «DUMAX | …», etc.).
const API_BASE = (
  (import.meta.env.VITE_SG_REALTIME_API_BASE as string | undefined) ||
  "https://sg-realtime-backend-oyni7keu3a-uc.a.run.app"
).replace(/\/+$/, "");

export type SgOrigins = Record<string, string[]>;

const ORDER = ["v5", "wialon", "mapon"];
const LABEL: Record<string, string> = { v5: "v5", wialon: "Wialon", mapon: "Mapon" };

// Igual que sg-realtime (compute.js): en SG hay IMEIs con espacios pegados.
export const imeiKey = (imei: unknown) => String(imei ?? "").trim();

export const originsOfImei = (map: SgOrigins | undefined, imei: unknown) =>
  map?.[imeiKey(imei)] ?? [];

// Unión de los orígenes de varias unidades (p. ej. las de un usuario).
export function unionOrigins(lists: string[][]): string[] {
  const seen = new Set(lists.flat());
  return ORDER.filter((o) => seen.has(o));
}

// Origen de un usuario = unión de los de sus unidades.
export const originsOfImeis = (map: SgOrigins | undefined, imeis: string[]) =>
  unionOrigins(imeis.map((i) => originsOfImei(map, i)));

// "v5 · Wialon" para el CSV; "" si no está en ninguna.
export const formatOrigins = (list: string[]) =>
  list.map((o) => LABEL[o] ?? o).join(" · ");

export async function fetchOrigins(imeis: string[]): Promise<SgOrigins> {
  const clean = [...new Set(imeis.map(imeiKey).filter(Boolean))];
  if (!clean.length) return {};
  const res = await fetch(`${API_BASE}/reporting/origins`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await sgRealtimeAuthHeaders()) },
    body: JSON.stringify({ imeis: clean }),
  });
  if (!res.ok) throw new Error(`Plataformas: sg-realtime respondió ${res.status}`);
  return (await res.json()).origins ?? {};
}

export function useSgOrigins(imeis: string[]) {
  const key = imeis.map(imeiKey).filter(Boolean).sort().join(",");
  return useQuery({
    queryKey: ["sg-origins", key],
    queryFn: () => fetchOrigins(imeis),
    enabled: key.length > 0,
    staleTime: 5 * 60_000,
  }).data;
}
